/**
 * Bundle 78 Task 1 - Public Missed Connection request form.
 *
 * URL: /introductions/request/:slug
 *
 * Anyone can submit a missed connection tied to a specific event.
 * The submission lands as an `introductions` row in `pending_review`
 * status and enters the admin curation queue.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Check } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const intApi = axios.create({ baseURL: `${BASE}/api` });

const CONTACT_PREF_OPTS = [
  { key: 'email', label: 'Email me first' },
  { key: 'phone', label: 'Phone me first' },
  { key: 'either', label: 'Either is fine' },
];

const MissedConnectionRequestPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    description: '',
    when_where: '',
    contact_pref: 'email',
    reporter_first_name: '',
    reporter_email: '',
  });

  useEffect(() => {
    let mounted = true;
    intApi.get(`/introductions/request/${slug}`)
      .then((r) => { if (mounted) setEvent(r.data?.event); })
      .catch((e) => { if (mounted) setError(e?.response?.data?.detail || 'Event not found.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: typeof v === 'string' ? v : v?.target?.value ?? v }));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await intApi.post(`/introductions/request/${slug}`, form);
      setSubmitted(true);
      toast.success('Thanks. Our team will review and reach out if we can match you.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not submit. Please retry.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (error && !event) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6" data-testid="missed-connection-error">
      <div className="max-w-md text-center">
        <div className="text-primary-900 font-semibold text-lg mb-2">This event is not available.</div>
        <div className="text-neutral-600 text-sm">{error}</div>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen bg-neutral-50" data-testid="missed-connection-thankyou">
      <div className="max-w-2xl mx-auto p-6 pt-16 text-center">
        <div className="inline-flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <Check className="h-4 w-4" /> Received
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-3">Thank you.</h1>
        <p className="text-primary-700 mt-3">We will review this quietly and reach out to both parties before making any introduction.</p>
      </div>
    </div>
  );

  const canSubmit = form.description.trim().length >= 8
    && form.reporter_first_name.trim()
    && form.reporter_email.trim();

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="missed-connection-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5" /> {event?.title}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-primary-900 mt-2">
          A missed connection
        </h1>
        <p className="text-primary-700 text-sm mt-2 max-w-xl">
          If you crossed paths with someone at this event and want us to help you say hello, tell us
          a little about them. We will vet carefully, ask both sides, and only introduce if both
          welcome it.
        </p>

        <div className="mt-8 space-y-5">
          <div>
            <label className="text-sm font-medium text-primary-900 leading-snug block mb-1">Describe who you noticed <span className="text-red-500">*</span></label>
            <Textarea
              value={form.description} onChange={set('description')}
              placeholder="Curly-haired friend of the host, wearing a linen jacket, told a great story about Kashmir." className="min-h-[110px]"
              data-testid="missed-connection-description"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-primary-900 leading-snug block mb-1">Where or when did you notice them?</label>
            <Input
              value={form.when_where} onChange={set('when_where')}
              placeholder="Near the bar, during the second half."
              data-testid="missed-connection-when-where"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-primary-900 leading-snug block mb-2">If we can match you both, how should we introduce?</label>
            <div className="flex gap-2 flex-wrap" data-testid="missed-connection-contact-pref">
              {CONTACT_PREF_OPTS.map((o) => (
                <button
                  key={o.key} type="button"
                  onClick={() => set('contact_pref')(o.key)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    form.contact_pref === o.key ? 'bg-primary-900 text-white border-primary-900'
                      : 'bg-white text-primary-800 border-neutral-200 hover:border-primary-400'
                  }`}
                  data-testid={`missed-connection-contact-pref-${o.key}`}
                >{o.label}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-primary-900 leading-snug block mb-1">Your first name <span className="text-red-500">*</span></label>
              <Input
                value={form.reporter_first_name} onChange={set('reporter_first_name')}
                data-testid="missed-connection-first-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-primary-900 leading-snug block mb-1">Your email <span className="text-red-500">*</span></label>
              <Input
                type="email" value={form.reporter_email} onChange={set('reporter_email')}
                data-testid="missed-connection-email"
              />
            </div>
          </div>
          {error ? (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="missed-connection-form-error">{error}</div>
          ) : null}
          <div className="flex justify-end">
            <Button
              onClick={submit} disabled={!canSubmit || submitting}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="missed-connection-submit-btn"
            >{submitting ? 'Sending...' : 'Send request'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissedConnectionRequestPage;
